import { SlidersHorizontal } from 'lucide-react'
import { useState, type FormEvent } from 'react'

import { useProviders, useSettings, useUpdateSettings } from '@/api/hooks'
import type { UserSettings } from '@/api/types'
import { Button, Input, Skeleton, toast } from '@/components/ui'
import { fieldErrorOf, generalErrorOf } from '@/features/auth/authErrors'

import { Callout } from './Callout'
import { SelectField, type SelectOption } from './SelectField'
import { SettingsSection } from './SettingsSection'
import { SettingsDisclosure } from './SettingsDisclosure'
import { ToggleField } from './ToggleField'

type Quality = UserSettings['audio_quality']
type PreferredProvider = UserSettings['preferred_provider']

const LOSSLESS_OPTIONS: readonly SelectOption<'preferred' | 'required'>[] = [
  { value: 'preferred', label: 'Prefer lossless' },
  { value: 'required', label: 'Require verified lossless' },
]

interface LibraryDraft {
  library_path: string
  playback_cache_days: string
  audio_quality: Quality
  lossless_policy: 'preferred' | 'required'
  allow_lossy_fallback: boolean
  preferred_provider: PreferredProvider
}

function toDraft(settings: UserSettings): LibraryDraft {
  return {
    library_path: settings.library_path,
    playback_cache_days: String(settings.playback_cache_days ?? 7),
    audio_quality: settings.audio_quality,
    lossless_policy: settings.lossless_policy ?? 'preferred',
    allow_lossy_fallback: settings.allow_lossy_fallback ?? false,
    preferred_provider: settings.preferred_provider,
  }
}

/**
 * Mirrors the server's rule (a relative segment path, no `..`, no absolutes) so
 * an obvious mistake is caught before the round trip. The server still decides.
 */
function validateLibraryPath(raw: string): string | undefined {
  const value = raw.trim()
  if (!value) return 'Enter a folder name.'
  if (value.startsWith('/') || /^[a-zA-Z]:[\\/]/.test(value)) {
    return 'Use a relative folder name, not an absolute path.'
  }
  if (value.includes('\\')) return 'Use forward slashes to separate folders.'
  if (value.split('/').some((segment) => segment === '..')) {
    return '".." is not allowed — the folder must stay inside the music directory.'
  }
  if (value.split('/').some((segment) => segment.trim().length === 0)) {
    return 'Remove the empty or duplicated slash.'
  }
  if (/[\u0000-\u001f\u007f]/.test(value)) {
    return 'Remove control characters from the folder name.'
  }
  return undefined
}

function LibrarySkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
      <Skeleton className="h-11 w-full" />
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-11 w-full" />
      </div>
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-11 w-full" />
      </div>
      <Skeleton className="h-11 w-36" />
    </div>
  )
}

export function LibrarySection() {
  const { data: settings, isPending, isError, error, refetch, isFetching } = useSettings()
  const update = useUpdateSettings()
  const { data: providers = [] } = useProviders()
  const providerOptions: SelectOption<PreferredProvider>[] = [
    { value: 'auto', label: 'Automatic' },
    ...providers.map((provider) => ({ value: provider.slug, label: provider.name })),
  ]

  const [draft, setDraft] = useState<LibraryDraft | null>(null)
  const [pathTouched, setPathTouched] = useState(false)

  if (isPending) {
    return (
      <SettingsSection icon={SlidersHorizontal} title="Playback">
        <LibrarySkeleton />
      </SettingsSection>
    )
  }

  if (isError || !settings) {
    return (
      <SettingsSection icon={SlidersHorizontal} title="Playback">
        <Callout tone="danger" title="These settings could not be loaded">
          {generalErrorOf(error) ?? 'Try again in a moment.'}
        </Callout>
        <Button variant="secondary" className="mt-3" loading={isFetching} onClick={() => void refetch()}>
          Try again
        </Button>
      </SettingsSection>
    )
  }

  const saved = toDraft(settings)
  const current = draft ?? saved

  const trimmedPath = current.library_path.trim()
  const pathChanged = trimmedPath !== saved.library_path
  const dirty =
    pathChanged ||
    current.playback_cache_days !== saved.playback_cache_days ||
    current.audio_quality !== saved.audio_quality ||
    current.lossless_policy !== saved.lossless_policy ||
    current.allow_lossy_fallback !== saved.allow_lossy_fallback ||
    current.preferred_provider !== saved.preferred_provider

  const cacheDays = Number(current.playback_cache_days)
  const cacheError = !Number.isInteger(cacheDays) || cacheDays < 1 || cacheDays > 365
    ? 'Enter a whole number from 1 to 365 days.' : undefined

  const clientPathError = validateLibraryPath(current.library_path)
  const serverPathError = fieldErrorOf(update.error, 'library_path')
  const pathError = serverPathError ?? (pathTouched ? clientPathError : undefined)

  const banner = generalErrorOf(update.isError ? update.error : null, [
    'library_path',
    'audio_quality',
    'lossless_policy',
    'allow_lossy_fallback',
    'preferred_provider',
    'playback_cache_days',
  ])

  function set<K extends keyof LibraryDraft>(key: K, value: LibraryDraft[K]) {
    setDraft({ ...current, [key]: value })
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setPathTouched(true)
    if (!dirty || update.isPending || clientPathError || cacheError) return

    const patch: Partial<UserSettings> = {}
    if (pathChanged) patch.library_path = trimmedPath
    if (current.playback_cache_days !== saved.playback_cache_days) patch.playback_cache_days = cacheDays
    if (current.audio_quality !== saved.audio_quality) patch.audio_quality = current.audio_quality
    if (current.preferred_provider !== saved.preferred_provider) {
      patch.preferred_provider = current.preferred_provider
    }

    if (current.lossless_policy !== saved.lossless_policy) patch.lossless_policy = current.lossless_policy
    if (current.allow_lossy_fallback !== saved.allow_lossy_fallback) patch.allow_lossy_fallback = current.allow_lossy_fallback

    const movedFolder = pathChanged
    update.mutate(patch, {
      onSuccess: () => {
        setDraft(null)
        setPathTouched(false)
        toast.success(
          'Library settings saved',
          movedFolder ? `New downloads will go to “${trimmedPath}”.` : undefined,
        )
      },
    })
  }

  return (
    <SettingsSection
      icon={SlidersHorizontal}
      title="Playback"
    >
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {banner ? (
          <Callout tone="danger" announce>
            {banner}
          </Callout>
        ) : null}

        <Input
          label="Keep temporary songs for (days)"
          type="number"
          min={1}
          max={365}
          step={1}
          value={current.playback_cache_days}
          onChange={(event) => set('playback_cache_days', event.target.value)}
          disabled={update.isPending}
          error={fieldErrorOf(update.error, 'playback_cache_days') ?? cacheError}
          hint="Since last play. Library and playlist songs stay permanently."
        />

        <SelectField
          label="Quality for new acquisitions"
          value={current.lossless_policy}
          options={LOSSLESS_OPTIONS}
          onChange={(value) => set('lossless_policy', value)}
          disabled={update.isPending}
          hint="Original imports are preserved as they are. Acquired files are measured before joining your library."
          error={fieldErrorOf(update.error, 'lossless_policy')}
        />

        <ToggleField
          label="Allow lossy fallback"
          description={current.lossless_policy === 'required'
            ? 'Required lossless never falls back to lossy audio.'
            : 'Allow a lossy file when verified lossless is unavailable.'}
          checked={current.allow_lossy_fallback && current.lossless_policy !== 'required'}
          onChange={(value) => set('allow_lossy_fallback', value)}
          disabled={update.isPending || current.lossless_policy === 'required'}
        />

        <SelectField
          label="Preferred audio provider"
          value={current.preferred_provider}
          options={providerOptions.some((option) => option.value === current.preferred_provider)
            ? providerOptions : [...providerOptions, { value: current.preferred_provider, label: `${current.preferred_provider} (unavailable)` }]}
          onChange={(value) => set('preferred_provider', value)}
          disabled={update.isPending}
          hint="Providers only acquire new files. Your library works without one."
          error={fieldErrorOf(update.error, 'preferred_provider')}
        />

        <SettingsDisclosure title="Advanced" forceOpen={!!pathError}>
          <div className="flex flex-col gap-2">
            <Input
              label="Library folder"
              value={current.library_path}
              onChange={(event) => set('library_path', event.target.value)}
              onBlur={() => setPathTouched(true)}
              placeholder="alex"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              disabled={update.isPending}
              error={pathError}
              hint={
                pathError
                  ? undefined
                  : 'Relative to the music directory, e.g. alex/music.'
              }
            />

            <p className="text-xs text-text-muted">
              Files land in{' '}
              <span className="font-mono text-text">
                music directory / {trimmedPath || '…'} / Artist / Album
              </span>
            </p>

            {pathChanged ? (
              <Callout tone="warning" title="Existing files are not moved">
                Songs you have already downloaded stay in{' '}
                <span className="font-mono">{saved.library_path}</span>. Only new downloads go to{' '}
                <span className="font-mono">{trimmedPath || '…'}</span>. Move the old folder yourself
                on the server if you want everything in one place.
              </Callout>
            ) : null}
          </div>
        </SettingsDisclosure>

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" loading={update.isPending} disabled={!dirty || !!cacheError}>
            Save changes
          </Button>
          {dirty ? (
            <>
              <Button
                variant="ghost"
                disabled={update.isPending}
                onClick={() => {
                  setDraft(null)
                  setPathTouched(false)
                  update.reset()
                }}
              >
                Discard
              </Button>
              <span className="text-sm text-text-muted">Unsaved changes</span>
            </>
          ) : null}
        </div>
      </form>
    </SettingsSection>
  )
}
